/**
 * web/src/components/settings/PurgeWorkflowsCard.tsx
 *
 * Testing-only card: "Clear all workflows". Deletes every workflow + its
 * approvals so the same HubSpot deals and monday speakers can be re-triggered
 * from scratch — cancelling a workflow does NOT free its triggerSource for a
 * re-run, only deleting the row does.
 *
 * Renders ONLY when the signed-in user is an admin AND the deploy is
 * non-production. Both conditions are re-checked on the server
 * (src/pixel/api/admin/purge.py), which refuses production for every role —
 * this gate just avoids showing a button that would 403.
 *
 * Deliberately shows NO "will delete N" preview. The only per-count source on
 * the client (useStats) is scoped to the ACTIVE EVENT, while the purge is
 * unscoped across every event on the deploy — a preview would understate the
 * blast radius. The dialog states the scope in words instead, and the toast
 * reports the real counts the server actually removed.
 *
 * Confirm uses the Radix <Dialog> wrapper, never window.confirm (UI-SPEC
 * anti-pattern). Theme tokens only — no raw palette utilities, no hex (UI-12).
 */
import { useState } from 'react';
import { useMe } from '../../hooks/queries/useMe';
import { usePurgeWorkflows } from '../../hooks/mutations/usePurgeWorkflows';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

/**
 * Renders the destructive "Clear all workflows" testing card, or nothing when
 * the caller isn't an admin on a non-production deploy.
 *
 * @returns the card, or null when gated out (also null while useMe is loading —
 *   a destructive button must never appear on an unresolved identity)
 * @sideEffects opens a Radix Dialog; on confirm fires the purge mutation
 *   (HTTP POST + cache teardown + toast)
 * @idempotent yes — re-running purges nothing and reports zeros
 */
export function PurgeWorkflowsCard() {
  const { data: me } = useMe();
  const purge = usePurgeWorkflows();
  const [confirming, setConfirming] = useState(false);

  // Fail closed: unresolved identity renders nothing rather than a live
  // destructive control.
  if (!me) return null;
  if (me.role !== 'admin') return null;
  if (me.deployEnv === 'production') return null;

  return (
    <>
      <Card>
        <Card.Head>
          <Icon name="alert" size={15} />
          <Card.Title>Testing tools</Card.Title>
          <span className="ml-auto text-[11px] uppercase tracking-[0.08em] font-semibold text-text-faint">
            {me.deployEnv}
          </span>
        </Card.Head>
        <Card.Body>
          <div className="text-[13px] font-medium text-text">Clear all workflows</div>
          <p className="mt-1.5 text-[12.5px] leading-[1.55] text-text-subtle">
            Deletes every workflow on this deploy — across all events — along with their
            steps, pending approvals, tracked emails, and audit history. Use it to re-run the
            same sponsors or speakers from a clean slate: cancelling a workflow doesn&apos;t
            free it to be triggered again, only deleting it does.
          </p>
          <p className="mt-2 text-[12.5px] leading-[1.55] text-text-subtle">
            Nothing outside Pixel is touched — monday rows, Drive files, Google Sheets, and
            already-sent email are all left exactly as they are. Your events, users, brands,
            and Gmail connections stay too.
          </p>
          <p className="mt-2 text-[11.5px] leading-[1.5] text-text-faint">
            This can&apos;t be undone, and it isn&apos;t available on production.
          </p>
          <div className="mt-4">
            <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
              <Icon name="x" size={14} />
              Clear all workflows
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Destructive confirm — Radix Dialog (not native confirm; UI-SPEC anti-pattern) */}
      <Dialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear every workflow?</DialogTitle>
            <DialogDescription>
              This deletes all workflows on this deploy, in every event, including any
              approvals currently waiting on someone. Their step history and audit trail go
              with them. It can&apos;t be undone — but nothing in monday, Drive, or your
              mailbox is affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {/* Cancel rendered FIRST so Radix focus management lands on the
                safer choice on open (UI-SPEC § Accessibility Contract). */}
            <Button
              variant="default"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={purge.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={purge.isPending}
              onClick={() => {
                purge.mutate(undefined, { onSettled: () => setConfirming(false) });
              }}
            >
              {purge.isPending ? 'Clearing…' : 'Yes, clear everything'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
